import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, Alert, Result } from 'antd';
import { LockOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';
import logoHorizontal from '../../../assets/images/logo-horizontal.svg';

const { Title, Text } = Typography;

const API_URL = (process.env as any).REACT_APP_API_URL || 'https://erp-backend-62ua.onrender.com/api';

const SetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const t = searchParams.get('token');
    if (!t) {
      setError('Invalid or missing invite link. Please contact your HR team.');
    } else {
      setToken(t);
    }
  }, [searchParams]);

  const onFinish = async (values: { password: string; confirm_password: string }) => {
    if (values.password !== values.confirm_password) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await apiClient.post(`/auth/set-password`, {
        token,
        password: values.password,
      });
      setSuccess(true);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px',
    }}>
      <Card style={{
        width: '100%',
        maxWidth: 440,
        borderRadius: 16,
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        border: 'none',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img
            src={logoHorizontal}
            alt="VetanaHR"
            style={{ height: 52, width: 'auto', marginBottom: 16, objectFit: 'contain' }}
          />
        </div>

        {success ? (
          <Result
            icon={<CheckCircleOutlined style={{ color: '#52c41a', fontSize: 56 }} />}
            title="Password Set Successfully!"
            subTitle="Your account is now active. You can log in using your email and the password you just set."
            extra={
              <Button
                type="primary"
                size="large"
                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none', borderRadius: 8, width: '100%' }}
                onClick={() => navigate('/login')}
              >
                Go to Login →
              </Button>
            }
          />
        ) : (
          <>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Title level={4} style={{ color: '#111827', margin: 0 }}>
                🔐 Set Your Password
              </Title>
              <Text type="secondary" style={{ fontSize: 14 }}>
                Welcome! Create a secure password to activate your account.
              </Text>
            </div>

            {error && (
              <Alert
                message={error}
                type="error"
                showIcon
                style={{ marginBottom: 20, borderRadius: 8 }}
              />
            )}

            {!token ? (
              <Alert
                message="Invalid or missing invite link. Please contact your HR team to resend the invite."
                type="error"
                showIcon
                style={{ borderRadius: 8 }}
              />
            ) : (
              <Form layout="vertical" onFinish={onFinish} size="large">
                <Form.Item
                  name="password"
                  label="New Password"
                  rules={[
                    { required: true, message: 'Please enter a password' },
                    { min: 8, message: 'Password must be at least 8 characters' },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: '#9ca3af' }} />}
                    placeholder="Create a strong password"
                    style={{ borderRadius: 8 }}
                  />
                </Form.Item>

                <Form.Item
                  name="confirm_password"
                  label="Confirm Password"
                  rules={[
                    { required: true, message: 'Please confirm your password' },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: '#9ca3af' }} />}
                    placeholder="Re-enter your password"
                    style={{ borderRadius: 8 }}
                  />
                </Form.Item>

                <div style={{ marginBottom: 16, padding: '10px 14px', background: '#f0f9ff', borderRadius: 8, border: '1px solid #bae6fd' }}>
                  <Text style={{ color: '#0369a1', fontSize: 13 }}>
                    💡 Use at least 8 characters. Mix letters, numbers, and symbols for a stronger password.
                  </Text>
                </div>

                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    style={{
                      width: '100%',
                      height: 48,
                      borderRadius: 8,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                      fontSize: 16,
                      fontWeight: 600,
                    }}
                  >
                    {loading ? 'Setting Password...' : 'Set Password & Activate Account'}
                  </Button>
                </Form.Item>
              </Form>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default SetPassword;
